import {
  Elements,
  PaymentRequestButtonElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import {
  loadStripe,
  PaymentRequest as StripePaymentRequest,
  PaymentRequestPaymentMethodEvent,
} from "@stripe/stripe-js";
import ApplePay from "icons/payments/ApplePay.png";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useUserData } from "store/slices/authSlice";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  getIntent,
  markIncompletePayment,
} from "store/slices/paymentsSlice";
import { decode } from "utils/HelperFunctions";
import { useTestAttrsFactory } from "utils/testid";
import { resolveStripePublishableKey } from "utils/stripePublishableKey";
import {
  applePayDebug,
  getApplePayEnvironment,
  maskClientSecret,
  summarizeStripeIntent,
} from "utils/applePayDebug";
import { BASE_URL, CREATE_CASHAPP_PAYMENT_PI } from "store/api/constApi";
import {
  getIntentOrderId,
  getPaymentFailureReason,
} from "utils/incompleteOrder";
import { getFailedIntentId } from "utils/stripeIntent";
import { buildWalletBillingDetails } from "utils/stripeBillingDetails";

interface ApplePayButtonProps {
  handleApplePay?: any;
  isRecurring?: boolean;
  isApplePay: boolean;
  setIsApplePay: (isAvailable: boolean) => void;
  amount: number | string;
  totalAmount?: number | string;
  email?: string;
  disabled?: boolean;
  isEdit?: boolean;
  isStepper?: boolean;
  isSchedulePayment?: boolean;
  setAmtErr?: any;
  setIsEdit?: any;
  useStripeApplePay?: boolean;
  clusterIdOverride?: string | null;
  leadId?: string | number | null;
  isPaymentLink?: boolean;
  paymentLinkId?: string | number | null;
  isAgreement?: boolean;
  agreementId?: string | number | null;
  isWalletProfile?: boolean;
  allowZeroAmount?: boolean;
  requestLabel?: string;
  slug?: string;
  customConv?: string | null;
}

const normalizeAmount = (value: number | string | undefined) =>
  Number(String(value ?? "").replace(/,/g, ""));

export const DisabledApplePayButton = () => {
  const testAttrs = useTestAttrsFactory();

  return (
    <div className="w-full mb-5">
      <button
        type="button"
        disabled
        aria-label="Apple Pay unavailable until a valid amount is entered"
        className="flex h-10 w-full cursor-not-allowed appearance-none items-center justify-center rounded-[5px] border-0 bg-black opacity-40"
        {...testAttrs("apple-pay-disabled", "Apple Pay unavailable")}
      >
        <img className="h-10 w-auto" src={ApplePay} alt="" aria-hidden="true" />
      </button>
    </div>
  );
};

const ApplePayButton = ({
  handleApplePay = () => {},
  isRecurring = false,
  isApplePay,
  setIsApplePay,
  amount,
  totalAmount,
  email,
  disabled = false,
  isEdit = false,
  isStepper = false,
  isSchedulePayment = false,
  useStripeApplePay = false,
  clusterIdOverride,
  leadId,
  isPaymentLink = false,
  paymentLinkId,
  isAgreement = false,
  agreementId,
  isWalletProfile = false,
  allowZeroAmount = false,
  requestLabel = "Online Payment",
  slug,
  customConv = "on",
}: ApplePayButtonProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const userData = useUserData();
  const { clusterId: encodedClusterId, id: encodedOrderId } = useParams();
  const routeClusterId = decode(encodedClusterId || "", true);
  const clusterId =
    clusterIdOverride ||
    routeClusterId ||
    decode(encodedOrderId || "")?.split("_")[1] ||
    userData?.data?.accountData?.clusterid;
  const dispatch = useAppDispatch();
  const testAttrs = useTestAttrsFactory();
  const disabledRef = useRef(disabled);
  const handleApplePayRef = useRef(handleApplePay);
  const isConfirmingRef = useRef(false);
  const activeOrderIdRef = useRef(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [paymentRequest, setPaymentRequest] =
    useState<StripePaymentRequest | null>(null);

  const principalAmount = normalizeAmount(amount);
  const displayAmount = normalizeAmount(totalAmount ?? amount);
  const isActualPaymentLink = Boolean(isPaymentLink && slug?.startsWith("pl_"));
  const amountIsValid =
    Number.isFinite(principalAmount) &&
    principalAmount >= (allowZeroAmount ? 0 : 1) &&
    Number.isFinite(displayAmount) &&
    displayAmount >= (allowZeroAmount ? 0 : 1);

  useEffect(() => {
    disabledRef.current = disabled;
  }, [disabled]);

  useEffect(() => {
    handleApplePayRef.current = handleApplePay;
  }, [handleApplePay]);

  useEffect(() => {
    applePayDebug("info", "button mounted", {
      ...getApplePayEnvironment(),
      useStripeApplePay,
      isRecurring,
      isStepper,
      isEdit,
      clusterId,
    });

    return () => applePayDebug("info", "button unmounted");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useStripeApplePay]);

  useEffect(() => {
    applePayDebug("info", "stripe readiness", {
      stripeLoaded: Boolean(stripe),
      elementsLoaded: Boolean(elements),
    });
  }, [stripe, elements]);

  useEffect(() => {
    if (!stripe || !elements) return;

    let isActive = true;
    setIsLoading(true);
    setPaymentRequest(null);

    // Wallet-profile SetupIntents intentionally use a zero total because no
    // charge is made. Invalid payment flows use a hidden fallback value only
    // for capability detection while their disabled button is displayed.
    const amountInCents = amountIsValid ? Math.round(displayAmount * 100) : 100;
    applePayDebug("info", "creating paymentRequest", {
      country: "US",
      currency: "usd",
      label: requestLabel,
      amountInCents,
      amountIsValid,
      principalAmount,
      displayAmount,
      isEdit,
    });
    const request = stripe.paymentRequest({
      country: "US",
      currency: "usd",
      total: {
        label: requestLabel,
        amount: amountInCents,
      },
      requestPayerName: true,
      requestPayerEmail: true,
      requestPayerPhone: true,
    });
    const completedEvents = new WeakSet<PaymentRequestPaymentMethodEvent>();
    let stripeConfirmationSucceeded = false;

    const completeOnce = (
      event: PaymentRequestPaymentMethodEvent,
      status: "success" | "fail"
    ) => {
      if (completedEvents.has(event)) return;
      event.complete(status);
      completedEvents.add(event);
    };

    // failedIntentId: the declined PaymentIntent, for pages that record the
    // failed payment through their own verify call.
    const notifyFailure = async (
      message?: string,
      markFailed = true,
      failedIntentId: string | null = null
    ) => {
      if (markFailed && activeOrderIdRef.current) {
        try {
          await dispatch(
            markIncompletePayment({
              orderId: activeOrderIdRef.current,
              cluster_id: clusterId || undefined,
              failed_reason: getPaymentFailureReason(message),
            })
          ).unwrap();
        } catch (markError) {
          applePayDebug("error", "unable to mark incomplete order failed", {
            orderId: activeOrderIdRef.current,
            message:
              markError instanceof Error ? markError.message : String(markError),
          });
        }
      }
      await handleApplePayRef.current(
        false,
        undefined,
        message,
        failedIntentId
      );
    };

    const confirmPayment = async (
      clientSecret: string,
      event: PaymentRequestPaymentMethodEvent
    ) => {
      applePayDebug("info", "confirming PaymentIntent", {
        paymentMethodId: event.paymentMethod.id,
        cardBrand: event.paymentMethod.card?.brand,
        walletType: event.paymentMethod.card?.wallet?.type,
      });
      const initialResult = await stripe.confirmCardPayment(
        clientSecret,
        { payment_method: event.paymentMethod.id },
        { handleActions: false }
      );

      if (initialResult.error || !initialResult.paymentIntent) {
        applePayDebug("error", "PaymentIntent failed", {
          code: initialResult.error?.code,
          declineCode: initialResult.error?.decline_code,
          message: initialResult.error?.message,
          type: initialResult.error?.type,
        });
        completeOnce(event, "fail");
        await notifyFailure(
          initialResult.error?.message,
          true,
          getFailedIntentId(initialResult.error, clientSecret)
        );
        return;
      }

      completeOnce(event, "success");
      let paymentIntent = initialResult.paymentIntent;
      applePayDebug(
        "info",
        "PaymentIntent response",
        summarizeStripeIntent(paymentIntent)
      );

      if (paymentIntent.status === "requires_action") {
        applePayDebug("info", "PaymentIntent requires_action, running 3DS");
        const actionResult = await stripe.confirmCardPayment(clientSecret);
        if (actionResult.error || !actionResult.paymentIntent) {
          applePayDebug("error", "PaymentIntent action failed", {
            code: actionResult.error?.code,
            declineCode: actionResult.error?.decline_code,
            message: actionResult.error?.message,
            type: actionResult.error?.type,
          });
          await notifyFailure(
            actionResult.error?.message,
            true,
            getFailedIntentId(actionResult.error, clientSecret)
          );
          return;
        }
        paymentIntent = actionResult.paymentIntent;
      }

      if (
        !paymentIntent.id.startsWith("pi_") ||
        paymentIntent.status !== "succeeded"
      ) {
        applePayDebug("error", "PaymentIntent not payable", {
          intentType: paymentIntent.id.split("_")[0],
          status: paymentIntent.status,
        });
        await notifyFailure(
          paymentIntent.status === "processing"
            ? "Your Apple Pay payment is still processing. Please do not retry yet."
            : undefined,
          paymentIntent.status !== "processing",
          paymentIntent.status !== "processing" ? paymentIntent.id : null
        );
        return;
      }

      applePayDebug(
        "info",
        "PaymentIntent confirmed, handing off to parent handler",
        summarizeStripeIntent(paymentIntent)
      );
      stripeConfirmationSucceeded = true;
      await handleApplePayRef.current(true, paymentIntent);
      applePayDebug("info", "parent handler finished (payment)");
    };

    const confirmSetup = async (
      clientSecret: string,
      event: PaymentRequestPaymentMethodEvent
    ) => {
      applePayDebug("info", "confirming SetupIntent", {
        paymentMethodId: event.paymentMethod.id,
        cardBrand: event.paymentMethod.card?.brand,
        walletType: event.paymentMethod.card?.wallet?.type,
      });
      const initialResult = await stripe.confirmCardSetup(
        clientSecret,
        { payment_method: event.paymentMethod.id },
        { handleActions: false }
      );

      if (initialResult.error || !initialResult.setupIntent) {
        applePayDebug("error", "SetupIntent failed", {
          code: initialResult.error?.code,
          declineCode: initialResult.error?.decline_code,
          message: initialResult.error?.message,
          type: initialResult.error?.type,
        });
        completeOnce(event, "fail");
        await notifyFailure(initialResult.error?.message);
        return;
      }

      completeOnce(event, "success");
      let setupIntent = initialResult.setupIntent;
      applePayDebug(
        "info",
        "SetupIntent response",
        summarizeStripeIntent(setupIntent)
      );

      if (setupIntent.status === "requires_action") {
        applePayDebug("info", "SetupIntent requires_action, running 3DS");
        const actionResult = await stripe.confirmCardSetup(clientSecret);
        if (actionResult.error || !actionResult.setupIntent) {
          applePayDebug("error", "SetupIntent action failed", {
            code: actionResult.error?.code,
            declineCode: actionResult.error?.decline_code,
            message: actionResult.error?.message,
            type: actionResult.error?.type,
          });
          await notifyFailure(actionResult.error?.message);
          return;
        }
        setupIntent = actionResult.setupIntent;
      }

      applePayDebug(
        "info",
        "SetupIntent confirmed, handing off to parent handler",
        summarizeStripeIntent(setupIntent)
      );
      stripeConfirmationSucceeded = true;
      await handleApplePayRef.current(true, setupIntent);
      applePayDebug("info", "parent handler finished (setup)");
    };

    const handlePaymentMethod = async (
      event: PaymentRequestPaymentMethodEvent
    ) => {
      applePayDebug("info", "paymentmethod event received (sheet authorised)", {
        paymentMethodId: event.paymentMethod?.id,
        paymentMethodType: event.paymentMethod?.type,
        cardBrand: event.paymentMethod?.card?.brand,
        cardLast4: event.paymentMethod?.card?.last4,
        cardFunding: event.paymentMethod?.card?.funding,
        cardCountry: event.paymentMethod?.card?.country,
        walletType: event.paymentMethod?.card?.wallet?.type,
        billingDetails: event.paymentMethod?.billing_details,
        payerEmail: event.payerEmail,
        payerName: event.payerName,
        payerPhone: event.payerPhone,
        walletName: event.walletName,
        methodName: event.methodName,
      });
      if (disabledRef.current || !amountIsValid || isConfirmingRef.current) {
        applePayDebug("warn", "paymentmethod ignored", {
          disabled: disabledRef.current,
          amountIsValid,
          alreadyConfirming: isConfirmingRef.current,
        });
        completeOnce(event, "fail");
        return;
      }

      isConfirmingRef.current = true;
      setIsConfirming(true);
      stripeConfirmationSucceeded = false;
      applePayDebug("info", "requesting Stripe intent", {
        gatewayMethod: useStripeApplePay ? "AP" : "legacy",
        intentKind: isRecurring ? "setup" : "payment",
        totalAmountCents: Math.round(principalAmount * 100),
        expectedTotalAmountCents: Math.round(displayAmount * 100),
        clusterId,
        isPaymentLink: isActualPaymentLink,
        isAgreement,
        isWalletProfile,
        isStepper,
      });

      const requestStartedAt = Date.now();
      try {
        // What the Apple Pay sheet returned, for the Stripe customer only. The
        // order's own name, email and phone stay as they were.
        const billing = buildWalletBillingDetails({
          payerName: event.payerName,
          payerEmail: event.payerEmail,
          payerPhone: event.payerPhone,
          address: event.paymentMethod?.billing_details?.address,
          email,
          account: userData?.data?.accountData,
        });
        const payload = {
          name: userData?.data?.accountData?.first_name,
          email: email ?? userData?.data?.accountData?.email,
          phone_number: userData?.data?.accountData?.phone,
          billing,
          description: "Online Payment",
          currency: "usd",
          payment_method_types: "card",
          isRecurring,
          isSchedulePayment,
          total_amount: Math.round(principalAmount * 100),
          expected_total_amount: Math.round(displayAmount * 100),
          accountid: userData?.data?.accountData?.accountid,
          cluster_id: clusterId,
          leadid: leadId,
          custom_conv: customConv || "off",
          isPaymentLink: isActualPaymentLink,
          payment_link_id: isActualPaymentLink ? paymentLinkId || null : null,
          isAgreement,
          agreement_id: agreementId || null,
          isWalletProfile,
          ...(isStepper
            ? {
                isRetry: true,
                order_id:
                  Number(decode(encodedOrderId || "")?.split("_")[0]) || 0,
              }
            : {}),
          ...(isActualPaymentLink && slug ? { slug } : {}),
          ...(isAgreement && slug ? { slug } : {}),
          ...(useStripeApplePay ? { gateway_method: "AP" } : {}),
        };
        applePayDebug("info", "API request: create-payment-intents", {
          method: "POST",
          url: `${BASE_URL}${CREATE_CASHAPP_PAYMENT_PI}`,
          intentKind: isRecurring ? "setup" : "payment",
          hasAuthToken: Boolean(localStorage.getItem("dcdial-customer-token")),
          payload,
        });
        const response: any = await dispatch(getIntent(payload));
        activeOrderIdRef.current = getIntentOrderId(response);
        const durationMs = Date.now() - requestStartedAt;
        const clientSecret = response?.payload?.body?.data?.client_secret;
        const responseGatewayMethod =
          response?.payload?.body?.data?.gateway_method;
        const responseBody = response?.payload?.body;
        const serverMsg =
          responseBody?.data?.msg || responseBody?.msg || undefined;
        const responseOk = Boolean(responseBody?.success);
        applePayDebug(
          responseOk ? "info" : "error",
          "API response: create-payment-intents",
          {
            durationMs,
            httpStatus: response?.payload?.status,
            success: responseOk,
            thunkRejected: response?.meta?.requestStatus === "rejected",
            rejectedWithValue: Boolean(response?.meta?.rejectedWithValue),
            transportError: response?.error?.message,
            serverMessage: response?.payload?.message,
            message: serverMsg,
            gatewayMethod: responseGatewayMethod || "missing",
            intentType:
              typeof clientSecret === "string"
                ? clientSecret.split("_")[0]
                : "missing",
            body: responseBody
              ? {
                  ...responseBody,
                  data:
                    responseBody.data && typeof responseBody.data === "object"
                      ? {
                          ...responseBody.data,
                          client_secret: maskClientSecret(
                            responseBody.data.client_secret
                          ),
                        }
                      : responseBody.data,
                }
              : (response?.payload ?? null),
          }
        );

        if (
          !response?.payload?.body?.success ||
          !clientSecret ||
          (useStripeApplePay &&
            (responseGatewayMethod !== "AP" ||
              !String(clientSecret).startsWith(isRecurring ? "seti_" : "pi_")))
        ) {
          applePayDebug("error", "intent request rejected", {
            expectedGatewayMethod: useStripeApplePay ? "AP" : "legacy",
            responseGatewayMethod: responseGatewayMethod || "missing",
            message:
              response?.payload?.body?.data?.msg ||
              response?.payload?.body?.msg ||
              "Unable to create Stripe intent",
          });
          completeOnce(event, "fail");
          await notifyFailure();
          return;
        }

        if (isStepper) {
          applePayDebug("info", "stepper flow: delegating to parent handler");
          completeOnce(event, "success");
          await handleApplePayRef.current(
            clientSecret,
            event,
            stripe,
            elements
          );
        } else if (isRecurring) {
          await confirmSetup(clientSecret, event);
        } else {
          await confirmPayment(clientSecret, event);
        }
      } catch (error) {
        completeOnce(event, "fail");
        await notifyFailure(
          undefined,
          !stripeConfirmationSucceeded && !isStepper
        );
        applePayDebug("error", "confirmation exception", {
          message: error instanceof Error ? error.message : String(error),
          name: error instanceof Error ? error.name : "UnknownError",
        });
      } finally {
        isConfirmingRef.current = false;
        setIsConfirming(false);
      }
    };

    const handleCancel = () => {
      applePayDebug("info", "wallet sheet cancelled by user");
    };

    request.on("paymentmethod", handlePaymentMethod);
    request.on("cancel", handleCancel);
    applePayDebug("info", "canMakePayment check started");
    request
      .canMakePayment()
      .then((result) => {
        if (!isActive) return;

        const applePayAvailable = Boolean(result?.applePay);
        const availableMethods = Object.entries(result || {})
          .filter(([, isAvailable]) => isAvailable)
          .map(([method]) => method);
        applePayDebug(
          applePayAvailable ? "info" : "warn",
          "canMakePayment result",
          {
            applePayAvailable,
            canMakePayment: Boolean(result),
            availableMethods,
            nativeCanMakePayments:
              getApplePayEnvironment().nativeCanMakePayments,
            willRenderButton: applePayAvailable && amountIsValid && !isEdit,
          }
        );
        if (!result) {
          applePayDebug(
            "warn",
            "no wallet reported by Stripe. Check: Safari on macOS/iOS, HTTPS, a card in Wallet, and the domain registered under Stripe > Payment methods > Apple Pay"
          );
        } else if (!applePayAvailable) {
          applePayDebug(
            "warn",
            "a wallet is available but not Apple Pay. Most likely the domain is not registered for Apple Pay in Stripe, or the browser is not Safari"
          );
        }
        setIsApplePay(applePayAvailable);
        setPaymentRequest(
          applePayAvailable && amountIsValid && !isEdit ? request : null
        );
      })
      .catch((error) => {
        if (!isActive) return;

        applePayDebug("error", "canMakePayment failed", {
          message: error instanceof Error ? error.message : String(error),
          name: error instanceof Error ? error.name : "UnknownError",
        });
        setIsApplePay(false);
        setPaymentRequest(null);
      })
      .finally(() => {
        if (isActive) setIsLoading(false);
      });

    return () => {
      isActive = false;
      request.off("paymentmethod", handlePaymentMethod);
      request.off("cancel", handleCancel);
    };
  }, [
    amountIsValid,
    clusterId,
    dispatch,
    displayAmount,
    email,
    encodedOrderId,
    elements,
    isEdit,
    isAgreement,
    isWalletProfile,
    isRecurring,
    isActualPaymentLink,
    isSchedulePayment,
    isStepper,
    leadId,
    agreementId,
    allowZeroAmount,
    paymentLinkId,
    principalAmount,
    requestLabel,
    setIsApplePay,
    slug,
    stripe,
    useStripeApplePay,
    customConv,
    userData?.data?.accountData?.accountid,
    userData?.data?.accountData?.email,
    userData?.data?.accountData?.first_name,
    userData?.data?.accountData?.phone,
  ]);

  if (!amountIsValid || isEdit) return <DisabledApplePayButton />;

  if (isLoading && !paymentRequest) {
    return (
      <div
        aria-label="Preparing Apple Pay"
        className="flex h-10 w-full mb-5 items-center justify-center rounded-[5px] bg-black"
        role="status"
        {...testAttrs("apple-pay-loading", "Preparing Apple Pay")}
      >
        <div
          className="h-5 w-5 animate-spin rounded-full border-2 border-solid border-white border-r-transparent motion-reduce:animate-[spin_1.5s_linear_infinite]"
          aria-hidden="true"
        />
      </div>
    );
  }

  if (!paymentRequest || !isApplePay) return null;

  return (
    <div
      aria-disabled={disabled || isConfirming}
      className={
        disabled || isConfirming
          ? "pointer-events-none cursor-not-allowed opacity-60 mb-5"
          : "cursor-pointer mb-5"
      }
      {...testAttrs("apple-pay", "Apple Pay")}
    >
      <PaymentRequestButtonElement
        options={{
          paymentRequest,
          style: {
            paymentRequestButton: {
              height: "40px",
              theme: "dark",
              type: "default",
            },
          },
        }}
        onClick={(event) => {
          if (disabled || isConfirming) event.preventDefault();
        }}
        onReady={() => applePayDebug("info", "Apple Pay button rendered")}
      />
    </div>
  );
};

const PaymentButton = (props: ApplePayButtonProps) => {
  const { setIsApplePay, useStripeApplePay = false } = props;
  const paymentPermission = useAppSelector(
    (state) => state.payment.paymentPermission
  );
  const stripeKey = useStripeApplePay
    ? resolveStripePublishableKey(paymentPermission, "AP")
    : resolveStripePublishableKey(paymentPermission, "CC");
  const [stripePromise, setStripePromise] = useState<any>(null);

  useEffect(() => {
    if (stripeKey) {
      applePayDebug("info", "loading Stripe.js", {
        keyMode: stripeKey.startsWith("pk_live") ? "live" : "test",
        keyPrefix: stripeKey.slice(0, 12),
        keySource: useStripeApplePay ? "ap_gateway_name" : "cc_gateway_name",
        gateway: useStripeApplePay
          ? paymentPermission?.ap_gateway_name?.name
          : paymentPermission?.cc_gateway_name?.name,
        paymentApiUrl: BASE_URL,
        pageUrl: window.location.href,
        applePaySettings: {
          ap_gateway: paymentPermission?.ap_gateway_name?.name,
          ap_gateway_id: paymentPermission?.ap_gateway_name?.id,
          ap_ext_availability: paymentPermission?.ap_ext_availability,
          ap_int_availability: paymentPermission?.ap_int_availability,
          cc_gateway: paymentPermission?.cc_gateway_name?.name,
          sameKeyAsCard:
            paymentPermission?.ap_gateway_name?.key ===
            paymentPermission?.cc_gateway_name?.key,
        },
      });
    }
    setStripePromise(stripeKey ? loadStripe(stripeKey) : null);
  }, [stripeKey, useStripeApplePay, paymentPermission]);

  useEffect(() => {
    if (!stripeKey) {
      applePayDebug(
        useStripeApplePay ? "error" : "warn",
        "Stripe publishable key unavailable; Apple Pay button will not render",
        {
          useStripeApplePay,
          apGateway: paymentPermission?.ap_gateway_name?.name,
          ccGateway: paymentPermission?.cc_gateway_name?.name,
        }
      );
    }
    if (useStripeApplePay && !stripeKey) {
      setIsApplePay(false);
    }
  }, [setIsApplePay, stripeKey, useStripeApplePay, paymentPermission]);

  if (!stripeKey || !stripePromise) return null;

  return (
    <Elements stripe={stripePromise}>
      <ApplePayButton {...props} />
    </Elements>
  );
};

export default PaymentButton;
